import { ArrowSquareOut, Camera, SlidersHorizontal, SpinnerGap } from '@phosphor-icons/react';
import { Button, Tag, TagFilter, VText } from '@vake/ui-kit';
import { Feature, FeatureCollection } from 'geojson';
import { FC, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { DetectionImage, PlanetThumbnail, TargetOfInterestImage } from '../../../shared/components/Popup';
import constants from '../../../shared/constants';
import { CircleDot } from '../../../shared/styles/CircleDot';
import { VakeClipboard } from '../../../utils/clipboard';
import { formatLatLonText } from '../../../utils/date.ts';
import { EOBrowserService, getEOBrowserUrl } from '../../../utils/eobrowser';
import { mmsiToFlag } from '../../../utils/mmsi-to-flag.tsx';
import { externalVesselUrl } from '../../../utils/mmsiExternalCall';
import { VakeShareButton } from '../../../utils/shareButton';
import { Arrow } from '../interactiveReport/reportInfo/ReportInfo';
import { Border, BorderFreeRounded, ColumnWrapper, Title } from '../styles/Wrappers';
import { SendToMattermost } from './send-to-mattermost.tsx';

const FocusedImageWrapper = styled.div`
    width: 100%;
    display: flex;
    justify-content: center;
    aligh-items: center;
`;

const FocusedImage = styled.div`
    display: flex;
    width: 100%;
    aspect-ratio: 1/1;
`;

const InformationContainer = styled.div`
    margin: 5px 5px 0;
    display: flex;
    flex-direction: column;
    gap: 5px;
    font-size: 12px;
`;

const Information = styled.div`
    padding: 10px;
    background-color: rgba(255, 255, 255, 0.05);
    display: flex;
    flex-direction: row;
    border-radius: 10px;
    flex-wrap: wrap;
    align-items: center;
    gap: 5px;
`;

const Banner = styled.div`
    display: flex;
    align-items: center;
    gap: 5px;
`;

const SpaceOccuiperContainer = styled.div`
    display: flex;
    gap: 8px;
`;

export const SideBar = styled.div`
    position: fixed;
    top: 50px;
    right: 5px;
    z-index: 1000;
    min-width: 175px;
    width: 20%;
    max-width: 325px;
    max-height: calc(100vh - 60px);
`;

const UnderTitle = styled.div`
    margin-top: 20px;
`;

const ImageHolder = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: space-between;
    height: 60vh;
    overflow-y: scroll;
    // padding-left: 5px;
`;

const ImageObjectWrapper = styled.span<{ $isSentinel?: boolean | null }>`
    display: flex;
    width: 95%;
    align-items: center;
    justify-content: space-between;
    border-radius: 5px;
    border: 2px solid transparent;
    background-color: ${(props) => (props.$isSentinel ? 'default' : '#1178aa')};
    &:hover {
        border: 2px solid #ecda80;
    }
    gap: 5px;
`;
const ImageWrapper = styled.div`
    width: 120px;
    height: 120px;
    display: inline-block;
    overflow: hidden;
    // width: 100%; /* Adjusts the width to fill the container */
    // height: auto; /* Maintains the aspect ratio */
    // object-fit: cover; /* Ensures the image covers the container without distortion */
    border-radius: 3px;
`;

const DateInfo = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    text-align: center;
    flex-grow: 1;
`;

export const TransformWrapper = styled.div<{ $farNorthScaling?: number | null }>`
    transform: scale(${(props) => (props.$farNorthScaling && props.$farNorthScaling > 69 ? 1.27 : 1.09)});
`;
const ButtonsHolder = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 5px;
`;

const ShowingIndeces = styled.div`
    margin-top: 20px;
    height: 20px;
    text-align: center;
`;
const Row = styled.div`
    display: flex;
    align-items: center;
    gap: 4px;
`;
const MicroText = styled.div`
    font-family: Detail D1, sans-serif;
    font-size: 12px;
    line-height: 16px;
    tracking: 0px;
    color: #8e8e93;
`;
const elevatedTags = ['Transshipment', 'Sanctioned', 'Problematic Flag'];
export const TagView = ({ tags }: { tags: string[] | null }) => {
    if (!tags) {
        // The tagging model is not ran
        return (
            <Row>
                <SpinnerGap size={10} color={'#8E8E93'} />
                <MicroText>{'tags awaiting'}</MicroText>
            </Row>
        );
    }
    return tags.map((tag: string, i: number) => (
        <Tag key={i} variant={elevatedTags.includes(tag) ? 'warning' : 'neutral'} size="small" subtle={false}>
            {tag}
        </Tag>
    ));
};

export const ShipInfo: FC<{
    selectedFeature: Feature | null;
    setBoatMetaData: (value: Feature | null) => void;
    shipName: string | null;
    upsampleImage?: boolean;
    url: string;
}> = ({ selectedFeature, setBoatMetaData, shipName, upsampleImage, url }) => {
    const getUrl = (service: EOBrowserService) => {
        return getEOBrowserUrl({
            date: selectedFeature!.properties!.timestamp.slice(0, 10),
            lat: selectedFeature!.properties!.lat,
            lng: selectedFeature!.properties!.lon,
            imageSource: selectedFeature!.properties!.image_source,
            service: service,
            zoom: 15,
        });
    };

    const formatTimestamp = (timestamp: string) => {
        const date = timestamp.split(' ');
        const datestring = date[1] + ' ' + date[2] + ' ' + date[3] + ', ' + date[4].slice(0, 5) + ' GMT';
        return datestring;
    };
    const mmsi = selectedFeature?.properties?.mmsi ? selectedFeature.properties.mmsi : null;
    const imo = selectedFeature?.properties?.imo ? selectedFeature.properties.imo : null;
    const formattedLatLonText = formatLatLonText(selectedFeature);
    return (
        <BorderFreeRounded>
            <Banner>
                <Arrow
                    onClick={() => {
                        setBoatMetaData(null);
                    }}
                >
                    <img src="/images/icons/ArrowLeft.svg" alt="arrow left" />
                </Arrow>
                <Title>{shipName}</Title>
                <VakeShareButton shareValue={url} />
                {mmsi ? (
                    <ArrowSquareOut
                        className="cursor-pointer"
                        size={20}
                        onClick={() => window.open(externalVesselUrl(mmsi), '_blank')}
                    />
                ) : null}
            </Banner>
            {selectedFeature && selectedFeature.properties ? (
                <>
                    <FocusedImageWrapper>
                        <FocusedImage>
                            {selectedFeature?.properties?.image_source !== 'planet_scope' ? (
                                selectedFeature.properties.image_path ? (
                                    <DetectionImage image_path={selectedFeature.properties.image_path} />
                                ) : (
                                    <TargetOfInterestImage
                                        feature={selectedFeature.properties}
                                        upsampleImage={upsampleImage || false}
                                    />
                                )
                            ) : selectedFeature.properties.image_path ? (
                                <PlanetThumbnail image_path={selectedFeature.properties.image_path} />
                            ) : null}
                        </FocusedImage>
                    </FocusedImageWrapper>
                    <InformationContainer>
                        <SpaceOccuiperContainer>
                            <TagView tags={selectedFeature.properties?.tags} />
                        </SpaceOccuiperContainer>
                        <SpaceOccuiperContainer>
                            {selectedFeature?.properties?.matching_label &&
                                selectedFeature?.properties?.object_class && (
                                    <Information>
                                        {getLegendDot(selectedFeature)}
                                        {`${selectedFeature.properties.matching_label} ${selectedFeature.properties.object_class}`}
                                    </Information>
                                )}
                        </SpaceOccuiperContainer>
                        {imo && (
                            <SpaceOccuiperContainer>
                                <Information>
                                    <VText>IMO {imo}</VText>
                                    <VakeClipboard infoValue={imo} />
                                </Information>
                            </SpaceOccuiperContainer>
                        )}
                        {mmsi && (
                            <SpaceOccuiperContainer>
                                <Information>
                                    <div className="flex gap-2 items-center">
                                        <VText>MMSI</VText>
                                        <VText>{mmsi ? mmsiToFlag(mmsi) : null}</VText>
                                        <VText>{mmsi}</VText>
                                    </div>
                                    <VakeClipboard infoValue={mmsi} />
                                </Information>
                            </SpaceOccuiperContainer>
                        )}
                        {typeof selectedFeature.properties.image_source === 'string' ? (
                            <SpaceOccuiperContainer>
                                <Information>
                                    <img
                                        src="/images/icons/satellite.svg"
                                        alt="no source"
                                        onDoubleClick={() => window.open(getUrl('copernicus'), '_blank')}
                                    />
                                    <VText>Satellite: {selectedFeature.properties?.image_source}</VText>
                                </Information>
                            </SpaceOccuiperContainer>
                        ) : null}
                        {formattedLatLonText ? (
                            <SpaceOccuiperContainer>
                                <Information>
                                    <VText>Lat, Lon: {formattedLatLonText}</VText>
                                    <VakeClipboard infoValue={formattedLatLonText} />
                                </Information>
                            </SpaceOccuiperContainer>
                        ) : null}
                        {typeof selectedFeature.properties?.est_speed === 'number' ? (
                            <SpaceOccuiperContainer>
                                <Information>
                                    <img src="/images/icons/speedometer.svg" alt="no speed" />
                                    <VText>AIS Speed: {selectedFeature.properties?.est_speed} kn</VText>
                                </Information>
                            </SpaceOccuiperContainer>
                        ) : null}
                        {typeof selectedFeature.properties?.ais_length === 'number' ? (
                            <SpaceOccuiperContainer>
                                <Information>
                                    <VText>AIS Length: {selectedFeature.properties?.ais_length} m</VText>
                                </Information>
                            </SpaceOccuiperContainer>
                        ) : null}
                        {typeof selectedFeature.properties?.length === 'number' ? (
                            <SpaceOccuiperContainer>
                                <Information>
                                    <VText>Predicted Length: {selectedFeature.properties?.length} m</VText>
                                </Information>
                            </SpaceOccuiperContainer>
                        ) : null}
                        {typeof selectedFeature.properties?.object_class_score == 'number' ? (
                            <SpaceOccuiperContainer>
                                <Information>
                                    <VText>
                                        Confidence: {Math.round(selectedFeature.properties?.object_class_score * 100)}%
                                    </VText>
                                </Information>
                            </SpaceOccuiperContainer>
                        ) : null}
                        {typeof selectedFeature.properties?.timestamp === 'string' ? (
                            <SpaceOccuiperContainer>
                                <Information>
                                    <img src="/images/icons/calendar.svg" alt="no timestamp" />
                                    <VText>
                                        {formatTimestamp(new Date(selectedFeature.properties?.timestamp).toUTCString())}
                                    </VText>
                                    <VakeClipboard infoValue={selectedFeature.properties?.timestamp} />
                                </Information>
                            </SpaceOccuiperContainer>
                        ) : null}
                        {selectedFeature.properties &&
                        typeof selectedFeature.properties?.image_path === 'string' &&
                        selectedFeature.properties?.image_source === 'sentinel2' ? (
                            <Button
                                variant="neutral"
                                onClick={() => {
                                    if (selectedFeature.properties) {
                                        const url =
                                            window.location.origin +
                                            '/clearview?insight_search_uuid=' +
                                            JSON.stringify([
                                                selectedFeature.properties.image_path
                                                    .split('sentinel2/')[1]
                                                    .split('.png')[0],
                                            ]);
                                        window.open(url, '_blank');
                                    }
                                }}
                                text="Use VAKE ClearView"
                            />
                        ) : null}
                        {selectedFeature.properties && (
                            <SendToMattermost
                                textOnButton="Order Report Image"
                                iconOnButton={<Camera size={16} />}
                                successMessage="Thank you, we've received your order. We'll send you the image soon."
                                textToSend={`I want a report image for insight uuid ${selectedFeature.properties.uuid}`}
                            />
                        )}
                    </InformationContainer>
                </>
            ) : (
                <InformationContainer>
                    <div style={{ height: '50px' }}>missing image information</div>
                </InformationContainer>
            )}
        </BorderFreeRounded>
    );
};

const getLegendDot = (feature: Feature) => {
    if (!feature.properties) return;
    if (feature.properties.object_class === 'platform' || feature.properties.object_class === 'windmill') {
        return <CircleDot {...constants.colors.markers.staticObjects} />;
    } else if (feature.properties.matching_label === 'undetected') {
        return <CircleDot {...constants.colors.markers.undetectedAis} />;
    } else if (feature.properties.matching_label === 'matched') {
        return <CircleDot {...constants.colors.markers.matchedVessels} />;
    } else if (feature.properties.matching_label === 'dark_vessel') {
        return <CircleDot {...constants.colors.markers.darkShips} />;
    }
};

export const OverwatchRight: FC<{
    aisPositions: FeatureCollection | undefined;
    matchedVessels: FeatureCollection | undefined;
    darkVessels: FeatureCollection | undefined;
    staticObjects: FeatureCollection | undefined;
    selectedFeature: Feature | null;
    setSelectedFeature: (value: Feature | null) => void;
    setHoveredFeature: (value: Feature | null) => void;
    shipName: string | null;
    detectionConfidence: number;
    url: string;
}> = ({
    aisPositions,
    matchedVessels,
    darkVessels,
    staticObjects,
    selectedFeature,
    setSelectedFeature,
    setHoveredFeature,
    shipName,
    detectionConfidence,
    url,
}) => {
    const [positions, setPositions] = useState<Feature[]>([]);
    const [numCaptures, setNumCaptures] = useState<number | null>(null);
    const [startIndex, setStartIndex] = useState<number>(0);
    const [endIndex, setEndIndex] = useState<number>(-1);
    const [isFilterOpen, setIsFilterOpen] = useState(false);

    const buttonIncrement = 30;

    const all_positions = useMemo(() => {
        const filtered_dark_vessels: FeatureCollection | undefined = darkVessels
            ? {
                  type: 'FeatureCollection',
                  features: darkVessels.features.filter(
                      (feature) => feature.properties?.object_class_score >= detectionConfidence / 100
                  ),
              }
            : undefined;
        return [
            ...(matchedVessels?.features || []),
            ...(filtered_dark_vessels?.features || []),
            ...(aisPositions?.features || []),
            ...(staticObjects?.features || []),
        ];
    }, [aisPositions, matchedVessels, darkVessels, staticObjects, detectionConfidence]);

    const noTag = 'No Tag';
    const awaitingTag = 'Awaiting Tag';
    const initialFilterState: Tag[] = useMemo(() => {
        const tags = all_positions.flatMap((feature) => feature.properties?.tags || []);
        tags.push(noTag, awaitingTag);
        const uniqueTags = [...new Set(tags)];
        return uniqueTags.map((tag) => ({
            children: tag,
            variant: elevatedTags.includes(tag) ? 'warning' : 'neutral',
            size: 'small',
            subtle: false,
        }));
    }, [all_positions]);
    const [filterState, setFilterState] = useState<Tag[]>(initialFilterState);

    useEffect(() => {
        setFilterState(initialFilterState);
    }, [initialFilterState]);

    const filteredPositions = useMemo(() => {
        const selectedTags = filterState.filter((tag) => !tag.subtle).map((tag) => tag.children);
        const hasNoTagFilter = selectedTags.includes(noTag);
        return all_positions.filter((event) => {
            if (event.properties?.tags) {
                const matchesNoTagFilter = hasNoTagFilter && event.properties?.tags.length === 0;
                const matchesTags = event.properties?.tags.some((eventTag: string) => selectedTags.includes(eventTag));
                return matchesTags || matchesNoTagFilter;
            }
            return selectedTags.includes(awaitingTag);
        });
    }, [all_positions, filterState]);

    const onFilterToogle = () => {
        setIsFilterOpen((prev) => !prev);
    };

    useEffect(() => {
        if (filteredPositions) {
            setPositions(filteredPositions);
            setNumCaptures(filteredPositions.length);
            setEndIndex(filteredPositions.length >= 30 ? 30 : filteredPositions.length);
            setStartIndex(0);
        }
    }, [filteredPositions]);

    return (
        <SideBar>
            {!selectedFeature ? (
                <BorderFreeRounded>
                    <Border>
                        <Title>Overwatch</Title>
                        <UnderTitle>{numCaptures ? numCaptures + ' satellite images' : '  '}</UnderTitle>
                        <div className="flex">
                            {isFilterOpen ? (
                                <TagFilter
                                    filter={filterState}
                                    setFilter={setFilterState}
                                    onClose={onFilterToogle}
                                    onApply={onFilterToogle}
                                />
                            ) : (
                                <Button
                                    onClick={onFilterToogle}
                                    text="Filter selection"
                                    iconAfter={<SlidersHorizontal size={16} />}
                                    variant="neutral"
                                    size="small"
                                />
                            )}
                        </div>
                    </Border>
                    <ImageHolder>
                        {filteredPositions.slice(startIndex, endIndex).map((item, index) => {
                            const isSentinel = item.properties?.image_source !== 'planet_scope'; // TODO: Needs to be updated if we add more sources
                            return (
                                <ImageObjectWrapper
                                    key={index}
                                    $isSentinel={isSentinel}
                                    onClick={() => {
                                        setSelectedFeature(item);
                                    }}
                                    onMouseEnter={() => setHoveredFeature(item)}
                                    onMouseLeave={() => setHoveredFeature(null)}
                                >
                                    <ImageWrapper>
                                        {isSentinel ? (
                                            <TransformWrapper $farNorthScaling={item.properties?.lat}>
                                                {item.properties?.image_path ? (
                                                    <DetectionImage image_path={item.properties.image_path} />
                                                ) : (
                                                    <TargetOfInterestImage feature={{ ...item.properties }} />
                                                )}
                                            </TransformWrapper>
                                        ) : item.properties?.image_path ? (
                                            <PlanetThumbnail image_path={item.properties.image_path} />
                                        ) : null}
                                    </ImageWrapper>
                                    {getLegendDot(item)}
                                    <ColumnWrapper>
                                        <div className="flex gap-2 items-center">
                                            <VText>
                                                {item.properties?.mmsi ? mmsiToFlag(item.properties?.mmsi) : null}
                                            </VText>
                                            <VText>{item.properties?.mmsi}</VText>
                                        </div>
                                        <DateInfo>
                                            {typeof item.properties?.timestamp === 'string' ? (
                                                <VText>
                                                    {new Date(item.properties?.timestamp).toUTCString().split(' ')[4] +
                                                        ' GMT'}
                                                </VText>
                                            ) : null}
                                        </DateInfo>
                                        <TagView tags={item.properties?.tags} />
                                    </ColumnWrapper>
                                </ImageObjectWrapper>
                            );
                        })}
                    </ImageHolder>
                    {numCaptures ? (
                        <BorderFreeRounded>
                            <ShowingIndeces>
                                Images {startIndex + 1} to {endIndex}
                            </ShowingIndeces>
                            <ButtonsHolder>
                                <Arrow
                                    disabled={startIndex === 0}
                                    onClick={() => {
                                        const newStartIndex =
                                            startIndex - buttonIncrement >= 0 ? startIndex - buttonIncrement : 0;
                                        const newEndIndex =
                                            newStartIndex <= 0
                                                ? buttonIncrement > positions.length
                                                    ? positions.length
                                                    : buttonIncrement
                                                : endIndex - buttonIncrement;

                                        setStartIndex(newStartIndex);
                                        setEndIndex(newEndIndex);
                                    }}
                                >
                                    <img src="/images/icons/ArrowLeft.svg" alt="Previous date" />
                                </Arrow>
                                <Arrow
                                    disabled={endIndex === positions.length}
                                    onClick={() => {
                                        const newEndIndex =
                                            endIndex + buttonIncrement <= numCaptures
                                                ? endIndex + buttonIncrement
                                                : numCaptures;
                                        const newStartIndex =
                                            newEndIndex >= numCaptures
                                                ? numCaptures - buttonIncrement < 0
                                                    ? 0
                                                    : numCaptures - buttonIncrement
                                                : startIndex + buttonIncrement;

                                        setStartIndex(newStartIndex);
                                        setEndIndex(newEndIndex);
                                    }}
                                >
                                    <img src="/images/icons/ArrowRight.svg" alt="right arrow" />
                                </Arrow>
                            </ButtonsHolder>
                        </BorderFreeRounded>
                    ) : null}
                </BorderFreeRounded>
            ) : (
                <ShipInfo
                    selectedFeature={selectedFeature}
                    setBoatMetaData={setSelectedFeature}
                    shipName={shipName}
                    url={url}
                />
            )}
        </SideBar>
    );
};

export default OverwatchRight;
